import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Box, Tabs, Tab, Paper, Container, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import {
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Web as WebIcon
} from '@mui/icons-material';
import PageContainer from '../../components/PageContainer';
import LoadingState from '../../components/LoadingState';
import AnalyticsOverview from '../../components/analytics/AnalyticsOverview';
import EmailPerformance from '../../components/analytics/EmailPerformance';
import LandingPages from '../../components/analytics/LandingPages';
import { twClasses } from '../../styles/tailwind-utils';
import { DateRange } from '../../services/analyticsService';
import { useUser } from '../../contexts/UserContext';
import { useApiService } from '../../services/apiService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-analytics-tabpanel-${index}`}
      aria-labelledby={`group-analytics-tab-${index}`}
      className="py-4"
      {...other}
    >
      {value === index && (
        <Box>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `group-analytics-tab-${index}`,
    'aria-controls': `group-analytics-tabpanel-${index}`,
  };
}

interface Group {
  id: number;
  name: string;
  description: string | null;
}

interface Client {
  id: number;
  agency_name: string;
}

const GroupAnalytics: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin } = useUser();
  const api = useApiService();
  
  // Redirect non-admin users
  React.useEffect(() => {
    if (!isAdmin()) {
      navigate('/app/welcome');
    }
  }, [isAdmin, navigate]);
  
  // Don't render if not admin
  if (!isAdmin()) {
    return null;
  }
  
  // Map tab names to indices
  const tabMap = {
    'overview': 0,
    'email-performance': 1,
    'landing-pages': 2
  } as const;

  // Map indices to tab names
  const reverseTabMap = {
    0: 'overview',
    1: 'email-performance',
    2: 'landing-pages'
  } as const;

  type TabValue = 0 | 1 | 2;

  const [value, setValue] = useState<TabValue>(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabParam in tabMap) {
      return tabMap[tabParam as keyof typeof tabMap];
    }
    return 0; // Default to overview
  });

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);  // last 30 days
    return {
      date_range: 'last_30_days',
      startDate: start,
      endDate: end
    };
  });

  // Group and client selection state
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupClients, setGroupClients] = useState<Client[]>([]);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserGroups();
  }, []);

  const fetchUserGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/groups');
      const userGroups = response.data;
      
      if (userGroups.length === 1) {
        // Auto-select if user is in only one group
        setSelectedGroup(userGroups[0]);
        await fetchGroupClients(userGroups[0].id);
        await checkGroupRole(userGroups[0].id);
      } else if (userGroups.length > 1) {
        // User is in multiple groups, they need to select one
        setGroups(userGroups);
      }
    } catch (err) {
      console.error('Error fetching user groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupClients = async (groupId: number) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/clients`);
      setGroupClients(response.data.map((gc: any) => gc.client));
    } catch (err) {
      console.error('Error fetching group clients:', err);
    }
  };

  const checkGroupRole = async (groupId: number) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/members`);
      const currentUser = response.data.find((member: any) => member.user?.email === user?.email);
      setIsGroupAdmin(currentUser?.role === 'admin');
    } catch (err) {
      console.error('Error checking group role:', err);
      setIsGroupAdmin(false);
    }
  };

  const handleGroupChange = async (groupId: number) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroup(group);
      setSelectedClientId(null); // Reset client selection when group changes
      await fetchGroupClients(groupId);
      await checkGroupRole(groupId);
    }
  };

  const handleClientChange = (clientId: number | null) => {
    setSelectedClientId(clientId);
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    // For custom date ranges, use the provided dates
    if (newDateRange.date_range === 'custom') {
      setDateRange(newDateRange);
      return;
    }

    // Calculate dates based on range type
    const end = new Date();
    const start = new Date();

    switch (newDateRange.date_range) {
      case 'last_7_days':
        start.setDate(end.getDate() - 7);
        break;
      case 'last_30_days':
        start.setDate(end.getDate() - 30);
        break;
      case 'last_90_days':
        start.setDate(end.getDate() - 90);
        break;
      case 'last_12_months':
        start.setMonth(end.getMonth() - 12);
        break;
      default:
        start.setDate(end.getDate() - 30); // default to last 30 days
    }

    // Set time to start of day for start date and end of day for end date
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    setDateRange({
      date_range: newDateRange.date_range,
      startDate: start,
      endDate: end
    });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setValue(newValue);
    // Update URL with new tab
    const newTab = reverseTabMap[newValue];
    navigate(`/app/group-analytics?tab=${newTab}`, { replace: true });
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState message="Loading group analytics..." variant="page" size="medium" />
      </PageContainer>
    );
  }

  if (!selectedGroup) {
    return (
      <PageContainer>
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          Group Analytics
        </Typography>
        <Paper elevation={2} className="p-6">
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please select a group to view analytics:
          </Typography>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel id="group-select-label">Select Group</InputLabel>
            <Select
              labelId="group-select-label"
              id="group-select"
              value=""
              label="Select Group"
              onChange={(e) => handleGroupChange(e.target.value as number)}
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Group Analytics: {selectedGroup.name}
        </Typography>
        
        {/* Client selector for group admins */}
        {isGroupAdmin && (
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id="client-select-label">View Individual Agency</InputLabel>
            <Select
              labelId="client-select-label"
              id="client-select"
              value={selectedClientId || ''}
              label="View Individual Agency"
              onChange={(e) => handleClientChange(e.target.value as number || null)}
            >
              <MenuItem value="">
                <em>All Agencies (Group View)</em>
              </MenuItem>
              {groupClients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.agency_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Paper elevation={2} className="p-6">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Tabs 
              value={value} 
              onChange={handleChange} 
              variant="scrollable"
              scrollButtons="auto"
              className="bg-white"
            >
              <Tab 
                icon={<BarChartIcon />} 
                iconPosition="start" 
                label="Overview" 
                {...a11yProps(0)} 
                className={value === 0 ? "text-primary-600 font-medium" : ""}
              />
              <Tab 
                icon={<TimelineIcon />} 
                iconPosition="start" 
                label="Email Performance" 
                {...a11yProps(1)} 
                className={value === 1 ? "text-primary-600 font-medium" : ""}
              />
              <Tab 
                icon={<WebIcon />} 
                iconPosition="start" 
                label="Landing Pages" 
                {...a11yProps(2)} 
                className={value === 2 ? "text-primary-600 font-medium" : ""}
              />
            </Tabs>
            
            <TabPanel value={value} index={0}>
              <AnalyticsOverview 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange}
                groupId={selectedGroup.id}
                clientId={selectedClientId}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <EmailPerformance 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange}
                groupId={selectedGroup.id}
                clientId={selectedClientId}
              />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <LandingPages 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange}
                groupId={selectedGroup.id}
                clientId={selectedClientId}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </Paper>
    </PageContainer>
  );
};

export default GroupAnalytics;
